import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, ListGroup } from 'react-bootstrap';
import Fuse from 'fuse.js';
import { useTranslation } from 'react-i18next';
import { isConfigurable, vehicleRefOf, type Trim } from '../domain';
import { useVehicle } from '../api';
import { useConfiguratorState } from '../store';
import { extractErrorMessage, formatCurrency } from '../common';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { useConfiguratorTranslations } from '../translations';
import styles from './TrimSelector.module.scss';
import shared from '../configurator.module.scss';
import { useFuelFilter } from './useFuelFilter';
import { FuelChipRow } from './FuelChipRow';

const TRIM_COLLAPSED_LIMIT = 10;

function useTrimSearch(trims: Trim[], query: string) {
	const fuse = useMemo(
		() =>
			new Fuse(trims, {
				keys: ['make', 'model', 'name'],
				threshold: 0.35,
				includeScore: false,
			}),
		[trims]
	);

	const searchedTrims = query.trim()
		? fuse.search(query).map((r) => r.item)
		: trims;

	return { searchedTrims };
}

/**
 * Chiave marca/modello per cui l'auto-selezione è già avvenuta.
 */
let autoSelectedFor: string | null = null;

/**
 * Da chiamare ogni volta che l'utente (ri)sceglie un modello, così una nuova
 * visita — anche allo stesso marca/modello di prima — auto-seleziona di
 * nuovo. Usata anche dai test per isolare i casi fra loro.
 */
export function resetAutoSelectedTrim(): void {
	autoSelectedFor = null;
}

export function useAutoSelectFirstTrim(trims: Trim[]) {
	const makeCode = useConfiguratorState((s) => s.makeCode);
	const modelCode = useConfiguratorState((s) => s.modelCode);
	const vehicle = useConfiguratorState((s) => s.vehicle);
	const setVehicle = useConfiguratorState((s) => s.setVehicle);

	useEffect(() => {
		if (!trims.length) return;
		const key = `${makeCode ?? ''} ${modelCode ?? ''}`;
		if (autoSelectedFor === key) return;
		autoSelectedFor = key;
		if (vehicle) return;

		const first = trims.find(isConfigurable);
		if (first) setVehicle(vehicleRefOf(first));
	}, [trims, makeCode, modelCode, vehicle, setVehicle]);
}

function TrimRow({ trim }: { trim: Trim }) {
	const vehicle = useConfiguratorState((s) => s.vehicle);
	const setVehicle = useConfiguratorState((s) => s.setVehicle);
	const clearVehicle = useConfiguratorState((s) => s.clearVehicle);
	const tr = useConfiguratorTranslations();
	const { t } = useTranslation();

	const configurable = isConfigurable(trim);
	const isSelected = configurable && vehicle?.eurotaxCode === trim.eurotaxCode;

	const onSelect = () => {
		if (!configurable) return;
		if (isSelected) clearVehicle();
		else setVehicle(vehicleRefOf(trim));
	};

	const notConfigurableMsg = t('car_configurator_trim_not_configurable', {
		defaultValue:
			'Questo allestimento non è ancora associato a un veicolo Motornet e non può essere configurato.',
	});
	const anchorId = `mn-trim-${String(trim.id).replace(/[^\w-]/g, '-')}`;

	return (
		<ListGroup.Item
			action={configurable}
			active={isSelected}
			disabled={!configurable}
			onClick={onSelect}
			className={`d-flex align-items-center gap-3 ${styles.trimRow}`}
		>
			<span
				id={configurable ? undefined : anchorId}
				style={configurable ? undefined : { pointerEvents: 'auto' }}
				className="flex-grow-1 fw-medium"
			>
				{trim.make} {trim.model} {trim.name}
			</span>
			<span className={styles.trimPrice}>
				{tr.trimPricePrefix}{' '}
				<strong>{formatCurrency(Number(trim.listPrice))}</strong>
			</span>
			{!configurable && (
				<ToolTip anchorId={anchorId} content={notConfigurableMsg} place="top" />
			)}
		</ListGroup.Item>
	);
}

function TrimVehicleError() {
	const vehicle = useConfiguratorState((s) => s.vehicle);
	const vehicleQuery = useVehicle(vehicle);
	const tr = useConfiguratorTranslations();

	if (!vehicleQuery.isError) return null;

	const detail = extractErrorMessage(vehicleQuery.error);

	return (
		<Alert variant="danger" className={`${shared.error} mb-3 mt-3`}>
			<div>{detail ? tr.loadingErrorDetail(detail) : tr.loadingError}</div>
			<Button
				size="sm"
				variant="outline-danger"
				className="ms-auto flex-shrink-0"
				onClick={() => vehicleQuery.refetch()}
			>
				{tr.retry}
			</Button>
		</Alert>
	);
}

export function TrimList({ trims, search }: { trims: Trim[]; search: string }) {
	useAutoSelectFirstTrim(trims);
	const [expanded, setExpanded] = useState(false);
	const {
		chips,
		activeChip,
		setActiveChip,
		filtered: filteredTrims,
	} = useFuelFilter(trims, (t) => t.fuel);
	const { searchedTrims } = useTrimSearch(filteredTrims, search);
	const visibleTrims = expanded
		? searchedTrims
		: searchedTrims.slice(0, TRIM_COLLAPSED_LIMIT);
	const tr = useConfiguratorTranslations();

	return (
		<div>
			<FuelChipRow
				chips={chips}
				activeChip={activeChip}
				onSelect={(chip) => {
					setActiveChip(chip);
					setExpanded(false);
				}}
			/>

			<div className={shared.filterGroupLabel}>{tr.trimSectionLabel}</div>
			{searchedTrims.length === 0 ? (
				<div className={styles.emptyMsg}>{tr.trimEmpty}</div>
			) : (
				<>
					<ListGroup>
						{visibleTrims.map((trim) => (
							<TrimRow key={trim.id} trim={trim} />
						))}
					</ListGroup>
					{!expanded && searchedTrims.length > TRIM_COLLAPSED_LIMIT && (
						<ListGroup className="mt-3">
							<ListGroup.Item
								action
								onClick={() => setExpanded(true)}
								className="text-center text-muted fst-italic py-3"
							>
								{tr.trimShowAll(searchedTrims.length)}
							</ListGroup.Item>
						</ListGroup>
					)}
				</>
			)}

			<TrimVehicleError />
		</div>
	);
}
